import {
  resolveVerticalStack,
  mirrorCenterX,
  type Rect,
  type VerticalStackSpec,
} from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { resolveOriginX } from "./directionalUi.js";
import dungeonSpec from "../data/layouts/dungeon.json";

// 던전 레이아웃 — title/info 세로 영역(stack) + 도전(좌)/소탕(우) 버튼 중심 offset.
const SPEC = dungeonSpec as unknown as {
  stack: VerticalStackSpec["children"];
  buttonY: number;
  challengeX: number;
  sweepXFromRight: number;
};

export interface DungeonLayout {
  readonly title: Rect;
  /** 제목 아래 팁 캐러셀 영역 (전체폭) — TipCarouselView 배치 기준(스탯 탭 동형). */
  readonly tip: Rect;
  readonly info: Rect;
  /** 텍스트 origin x — LTR 0(좌)/RTL 1(우). title/info 정렬에 사용. */
  readonly textOriginX: number;
  /** 도전 버튼 중심 (NineSliceButton origin 0.5). */
  readonly challenge: { x: number; y: number };
  /** 소탕 버튼 중심. */
  readonly sweep: { x: number; y: number };
}

/**
 * 던전 탭 좌표 계산 (순수). 배치 수치는 dungeon.json 선언.
 * RTL이면 도전/소탕 버튼을 좌우 미러(mirrorCenterX) + 텍스트를 영역 우측 끝 정렬(origin 1).
 * LTR 값은 기존과 동일 — 회귀 0.
 */
export function computeDungeonLayout(content: Rect, dir: TextDirection = "ltr"): DungeonLayout {
  const areas = resolveVerticalStack({ children: SPEC.stack }, content);
  const { title, tip, info } = areas;
  if (!title || !tip || !info) {
    throw new Error("dungeon.json: title/tip/info id가 필요합니다");
  }
  const rtl = dir === "rtl";
  return {
    // 텍스트는 RTL이면 영역 우측 끝(origin 1)에 정렬 — x를 우측 경계로, LTR은 기존 좌측.
    title: rtl ? { ...title, x: title.x + title.width } : title,
    // 팁 캐러셀은 전체폭 컨테이너라 방향 무관(content.x/width 그대로).
    tip: { x: content.x, y: tip.y, width: content.width, height: tip.height },
    info: rtl ? { ...info, x: info.x + info.width } : info,
    textOriginX: resolveOriginX(dir),
    challenge: {
      x: content.x + mirrorCenterX(SPEC.challengeX, content.width, dir),
      y: content.y + SPEC.buttonY,
    },
    sweep: {
      x: content.x + mirrorCenterX(content.width - SPEC.sweepXFromRight, content.width, dir),
      y: content.y + SPEC.buttonY,
    },
  };
}
